import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface FeudalHistoryPageProps {
}

const FeudalHistoryPage = (props: FeudalHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_pre_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Feudal Age - Chase the Fox`,
        description: "The Feudal Age of Casarok was a time in pre-Cataclysmic history when great kingdoms ruled the wold!",
        keywords: ["casarok", "casarok history", "feudal age", "fantasy timeline", "fantasy world history", "cataclysm fantasy", "pre-Cataclysmic era"],
        imgSrc: _map_src,
        url: `world/history/feudal-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Pre-Cataclysm Map'
                         alt="A map of Casarok before the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Feudal Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_pre_cataclysm.png`)}
                             title='Casarok Pre-Cataclysm Map'
                             alt="A map of Casarok before the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years 400 BCE - 85 ACE.</em>
                    </p>
                    <p>
                        The Feudal Age is the period of the world in which the world was ruled by large, powerful
                        kingdoms. Kings, emperors, and all sorts of leaders during this time claimed divine right via
                        the <NavLink className="world-link" to="/world/religion/pantheon">gods and goddesses</NavLink> of
                        Casarok. Because so many powerful kingdoms ruled over the world, there was much stability during
                        this time; regardless, there were still many wars and border conflicts that cost the lives of
                        many anthros. The most popular system of governance in this age was the feudal system which
                        gave this age its name. Many magical and technological advancements occurred during this time,
                        but the majority of them were focused on warfare and protection.
                    </p>
                    <p>
                        Tensions flared amongst the pantheon of Casarok as well. Two of the Dragon Deities, the brothers
                        Tianlong and Ragnarok, quarreled significantly in the later stages of the Feudal Age. Upset at
                        the desecration of his holy sites and the inaction of his brother, Ragnarok swore that he would
                        take out his vengeance on all mortals by enacting the Endtimes. Tianlong forbade him from doing
                        so as there was not enough consensus among the pantheon to wipe out all life on the planet.
                        Ragnarok felt that it was his decision alone as the god of death. The fighting continued for
                        a long time until Ragnarok went into the shadows to enlist the assistance of mortals to help him
                        craft the Astamortis, a spear forged of pure darkness with the hand of a mortal wrapped about it
                        that could allow one god to slay another. Ragnarok used the Astamortis to take Tianlong's life.
                        In doing so, he created a power vacuum among the rest of the pantheon that eventually led to
                        the <NavLink className="world-link" to='/world/history/cataclysmic-age'>War of the Gods</NavLink>.
                    </p>
                    <h3 className="headline">Notable Events</h3>
                    <ul className="spaced">
                        <li>
                            <em>389 BCE</em>: A great plague falls over Casarok, killing thousands of anthros.
                        </li>
                        <li>
                            <em>289 BCE</em>: The <NavLink className="world-link" to="/world/languages/common">Common language</NavLink> is
                            in use by 80% of the world's population.
                        </li>
                        <li>
                            <em>216 BCE</em>: <NavLink className="world-link" to="/characters/pyrrhe">Pyrrhe Drake</NavLink> is
                            born.
                        </li>
                        <li>
                            <em>197 BCE</em>: The counterweight trebuchet became one of the most popular non-magical
                            siege weapons used throughout the world.
                        </li>
                        <li>
                            <em>138 BCE</em>: Pyrrhe Drake becomes a knight of the Leonida Kingdom.
                        </li>
                        <li>
                            <em>129 BCE</em>: Pyrrhe Drake finds and is chosen by the legendary weapon, Draconis, making
                            him the champion of Tianlong.
                        </li>
                        <li>
                            <em>121 BCE</em>: Noboru Hayashi, also known
                            as <NavLink className="world-link" to="/characters/foxblade">Foxblade</NavLink>, is born. He
                            eventually becomes the final
                            nine-tailed <NavLink className="world-link" to="/world/bestiary/aspect/kitsune">kitsune</NavLink> in
                            Casarokan history.
                        </li>
                        <li>
                            <em>104 BCE</em>: The king of the Leonida Kingdom was assassinated through a plot
                            orchestrated by the champion of
                            Ragnarok, <NavLink className="world-link" to="/characters/blackfox">Blackfox</NavLink>.
                        </li>
                        <li>
                            <em>102 BCE</em>: Foxblade, having become the champion of the goddess Mother, slew Blackfox
                            before he could enact another of his schemes to complete his overthrow of the world order.
                        </li>
                        <li>
                            <em>93 BCE</em>: The kingdom of Leonida nears collapse when its land becomes split amongst
                            four duchies.
                        </li>
                        <li>
                            <em>85 BCE</em>: The leader of the gods, Tianlong, was slain at the hand of his brother
                            Ragnarok, a feat completely unthought of throughout the entire pantheon's history.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default FeudalHistoryPage;